import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Avatar,
  Grid,
} from '@mui/material';
import {
  Group as GroupIcon,
  RateReview as ReviewIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';

const RequestCard = ({ request, onReviewClick }) => {
  const getTypeLabel = (type) => {
    const labels = {
      partial_withdrawal: 'Partial Withdrawal',
      full_withdrawal: 'Full Withdrawal',
      account_offset: 'Account Offset',
      account_deactivation: 'Account Deactivation',
      savings_transfer: 'Savings Transfer',
      group_transfer: 'Group Transfer',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      partial_withdrawal: '#2196f3',
      full_withdrawal: '#f44336',
      account_offset: '#ff9800',
      account_deactivation: '#9c27b0',
      savings_transfer: '#4caf50',
      group_transfer: '#00bcd4',
    };
    return colors[type] || '#757575';
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'executed': return 'info';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => `KSh ${parseFloat(amount || 0).toLocaleString()}`;

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const isPending = request.status === 'pending';
  const typeColor = getTypeColor(request.type);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderTop: `4px solid ${typeColor}`,
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header: type + status */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Chip
            label={getTypeLabel(request.type)}
            size="small"
            sx={{ backgroundColor: typeColor, color: '#ffffff', fontWeight: 'bold' }}
          />
          <Chip
            label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
            color={getStatusChipColor(request.status)}
            size="small"
          />
        </Box>

        {/* Member Info */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: typeColor }}>
            {request.memberName?.charAt(0) || '?'}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
              {request.memberName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {request.memberNumber}
            </Typography>
          </Box>
        </Box>

        {/* Group */}
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}
        >
          <GroupIcon fontSize="small" />
          {request.currentGroup || request.groupName || 'No Group'}
        </Typography>

        {/* Request Details - Dynamic based on type */}
        <Box mb={2} flexGrow={1}>
          {request.type === 'partial_withdrawal' && (
            <>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', my: 1 }}>
                {formatCurrency(request.amount)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Balance: {formatCurrency(request.currentBalance)}
              </Typography>
            </>
          )}

          {request.type === 'full_withdrawal' && (
            <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', my: 1 }}>
              {formatCurrency(request.amount)}
            </Typography>
          )}

          {request.type === 'account_offset' && (
            <>
              <Typography variant="body2" color="textSecondary">
                Loan Balance: {formatCurrency(request.loanBalance)}
              </Typography>
              <Typography variant="h5" color="error" sx={{ fontWeight: 'bold', my: 1 }}>
                {formatCurrency(request.offsetAmount)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Offset amount
              </Typography>
            </>
          )}

          {request.type === 'account_deactivation' && (
            <Typography variant="body2" color="textSecondary">
              Current Balance: {formatCurrency(request.currentBalance)}
            </Typography>
          )}

          {request.type === 'savings_transfer' && (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                {request.fromAccount} &rarr; {request.toAccount}
              </Typography>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', my: 1 }}>
                {formatCurrency(request.amount)}
              </Typography>
            </>
          )}

          {request.type === 'group_transfer' && (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                Transferring to: {request.requestedGroup}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Current Balance: {formatCurrency(request.savingsBalance)}
              </Typography>
            </>
          )}
        </Box>

        {/* Dates / Officer */}
        <Box
          mb={2}
          sx={{
            backgroundColor: 'action.hover',
            p: 1.5,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary" display="block">
                Requested:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatDate(request.requestDate)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary" display="block">
                Officer:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                {request.fieldOfficer}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Action Button */}
        <Button
          size="small"
          variant="contained"
          color={isPending ? 'primary' : 'inherit'}
          startIcon={isPending ? <ReviewIcon /> : <ViewIcon />}
          onClick={() => onReviewClick(request)}
          fullWidth
        >
          {isPending ? 'Review' : 'View'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default RequestCard;